/**
 * Creates a cropped image from the provided image URL and crop area
 * @param {string} imageSrc - The source image URL or data URL
 * @param {Object} pixelCrop - The crop area in pixels {x, y, width, height}
 * @returns {Promise<Blob>} - Promise that resolves to the cropped image blob
 */
export const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Set canvas size to match crop area
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Fill canvas with white background (for transparent PNGs)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Convert canvas to blob (using PNG to preserve quality and transparency handling)
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(blob);
    }, 'image/png');
  });
};

/**
 * Creates an HTML Image element from a source URL
 * @param {string} url - The image URL or data URL
 * @returns {Promise<HTMLImageElement>} - Promise that resolves to the loaded image
 */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // Needed to avoid CORS issues
    image.src = url;
  });

/**
 * Converts a File to a data URL for preview
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Promise that resolves to the data URL
 */
export const readFile = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result));
    reader.readAsDataURL(file);
  });
